import React from 'react';

const SiteCard = ({ site, onBook }) => {
  const getCategoryIcon = (category) => {
    const categoryIcons = {
      'entry_ticket': '🎫',
      'parking_ticket': '🅿️',
      'chappal_stand_ticket': '👟',
      'puja_ticket': '🙏',
      'event_ticket': '🎭',
      'function_hall_ticket': '🏛️',
      'photo_shoot_ticket': '📸',
      'pass_ticket': '📅',
      'other': '🎫'
    };
    return categoryIcons[category] || '🎫';
  };

  const getCategoryLabel = (category) => {
    const categoryLabels = {
      'entry_ticket': 'Entry Ticket',
      'parking_ticket': 'Parking',
      'chappal_stand_ticket': 'Chappal Stand',
      'puja_ticket': 'Puja Service',
      'event_ticket': 'Event',
      'function_hall_ticket': 'Function Hall',
      'photo_shoot_ticket': 'Photo Shoot',
      'pass_ticket': 'Pass Ticket',
      'other': 'Other'
    };
    return categoryLabels[category] || 'Unknown';
  };

  const getCategoryGradient = (category) => {
    const gradients = {
      'entry_ticket': '#48bb78, #38a169',
      'parking_ticket': '#4299e1, #3182ce',
      'chappal_stand_ticket': '#ed8936, #dd6b20',
      'puja_ticket': '#ed64a6, #d53f8c',
      'event_ticket': '#9f7aea, #805ad5',
      'function_hall_ticket': '#f6ad55, #ed8936',
      'photo_shoot_ticket': '#38b2ac, #319795',
      'pass_ticket': '#14b8a6, #0d9488',
      'other': '#4facfe, #00f2fe'
    };
    return gradients[category] || '#4facfe, #00f2fe';
  };

  const getIcon = (name, category) => {
    // Prioritize category-specific icons
    if (category && getCategoryIcon(category) !== '🎫') {
      return getCategoryIcon(category);
    }
    
    // Fallback to name-based icons for generic categories
    if (name.toLowerCase().includes('beach')) return '🏖️';
    if (name.toLowerCase().includes('temple')) return '🛕';
    if (name.toLowerCase().includes('lake')) return '🏞️';
    if (name.toLowerCase().includes('park')) return '🌳';
    if (name.toLowerCase().includes('safari')) return '🦁';
    if (name.toLowerCase().includes('museum')) return '🏛️';
    return getCategoryIcon(category);
  };

  const handleCardClick = (e) => {
    // Prevent event bubbling from button clicks
    if (e.target.tagName === 'BUTTON') return;
    onBook(site);
  };

  return (
    <div 
      onClick={handleCardClick}
      style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: window.innerWidth <= 768 ? '16px' : '24px',
        padding: '0',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        cursor: 'pointer',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid rgba(255,255,255,0.2)',
        overflow: 'hidden',
        position: 'relative'
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
        e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.2)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.12)';
      }}
    >
      {/* Hero Image Area */}
      <div style={{
        height: window.innerWidth <= 768 ? '140px' : '180px',
        background: `linear-gradient(135deg, ${getCategoryGradient(site.category)})`,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          fontSize: window.innerWidth <= 768 ? '3rem' : '4rem',
          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
        }}>
          {getIcon(site.name, site.category)}
        </div>
        
        {/* Price Badge */}
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          padding: '8px 16px',
          borderRadius: '50px',
          fontWeight: '700',
          color: '#2d3748',
          fontSize: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          ₹{site.price}
        </div>
        
        {/* Category Badge */}
        <div style={{
          position: 'absolute',
          top: '16px',
          left: '16px',
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '6px 12px',
          borderRadius: '50px',
          fontSize: '12px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span>{getCategoryIcon(site.category)}</span>
          {getCategoryLabel(site.category)}
        </div>
      </div>
      
      {/* Content */}
      <div style={{ padding: window.innerWidth <= 768 ? '16px' : '24px' }}>
        <h3 style={{
          margin: '0 0 12px',
          fontSize: window.innerWidth <= 768 ? '1.1rem' : '1.25rem',
          fontWeight: '700',
          color: '#2d3748',
          lineHeight: '1.3'
        }}>
          {site.name}
        </h3>
        
        <p style={{
          color: '#718096',
          fontSize: window.innerWidth <= 768 ? '13px' : '14px',
          margin: window.innerWidth <= 768 ? '0 0 16px' : '0 0 20px',
          lineHeight: '1.5',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {site.description}
        </p>
        
        {/* Features */}
        <div style={{
          display: 'flex',
          gap: window.innerWidth <= 768 ? '6px' : '8px',
          marginBottom: window.innerWidth <= 768 ? '16px' : '20px',
          flexWrap: 'wrap'
        }}>
          <span style={{
            background: 'rgba(102, 126, 234, 0.1)',
            color: '#667eea',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            ⚡ Instant Booking
          </span>
          <span style={{
            background: 'rgba(72, 187, 120, 0.1)',
            color: '#48bb78',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            ✅ Confirmed
          </span>
        </div>
        
        {/* Book Button */}
        <button 
        onClick={(e) => {
          e.stopPropagation();
          onBook(site);
        }}
        style={{
          width: '100%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          border: 'none',
          padding: window.innerWidth <= 768 ? '12px' : '14px',
          borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
          fontSize: window.innerWidth <= 768 ? '14px' : '16px',
          fontWeight: '700',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
        }}
        onMouseOver={(e) => {
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.6)';
        }}
        onMouseOut={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
        }}
        >
          🎫 Book Experience
        </button>
      </div>
    </div>
  );
};

export default SiteCard;